import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation, withTranslation } from 'react-i18next';
import classnames from 'classnames';
import injectSheet from 'react-jss';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Checkbox,
  Modal,
  Typography,
} from 'antd';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import { useToggle } from '../../hooks';
import { DEFAULT_NO_YEAR, getDefaultBirthday } from '../../lib/birthdays';
import countryCodesList from '../../lib/country-codes.json';
import { inputProps, selectProps } from '../../lib/forms';
import { INVITATION_TYPES } from '../../lib/invitations';
import {
  formatNumber,
  isValidNumber,
  parseAsYouType,
  separateFormattedNumber,
} from '../../lib/phone-numbers';
import { birthdayPropType, classesPropType } from '../prop-types';
import BirthdayInput from '../Common/Forms/BirthdayInput';
import Input from '../Common/Forms/Input';
import Select from '../Common/Forms/Select';
import SubmitButton from '../Common/Forms/SubmitButton';
import { onlyDesktop, onlyMobile } from '../../lib/media-queries';
import theme from '../../theme/cupcake';
import CountryCodeSelect from './CountryCodeSelect';

const { Title } = Typography;

const styles = {
  centeredText: {
    textAlign: 'center',
  },
  submitButton: {
    margin: '5px 0',
  },
  label: {
    color: theme.brandPrimary,
  },
  nameContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  firstName: {
    marginRight: '3px',
    flex: 1,
  },
  lastName: {
    marginLeft: '2px',
    flex: 1,
  },
  countryCodeSelectDesktop: {
    margin: 0,
  },
  countryCodeSelectMobile: {
    margin: 0,
    width: '100%',
  },
  [onlyDesktop]: {
    countryCodeSelectMobile: {
      display: 'none',
    },
  },
  [onlyMobile]: {
    countryCodeSelectDesktop: {
      display: 'none',
    },
  },
  modal: {
    minHeight: '100%',
  },
};

const dialCodesOptions = countryCodesList.map(({
  name,
  flag,
  code,
  dialCode,
}) => ({
  key: code,
  label: `${flag} ${name} (${dialCode})`,
  value: code,
}));
const dialCodesOptionsByCountryCode = keyBy(dialCodesOptions, 'value');

const dialCodesByCountryCode = mapValues(keyBy(countryCodesList, 'code'), ({ dialCode }) => dialCode);

function AnswerRequestForm(props) {
  const {
    classes,
    handleChange,
    handleSubmit,
    inviterName,
    isSubmitting,
    isValid,
    setFieldValue,
    title,
    values,
    values: { countryCode: countryCodeValue, rememberMe },
  } = props;
  const { t } = useTranslation();

  const [countryCodeModalVisible, toggleCountryCodeModalVisible] = useToggle(false);

  const handleSelect = useCallback(value => setFieldValue('birthday', value), [setFieldValue]);
  const handlePhoneNumberChange = useCallback(
    (event) => {
      const separatedPhoneNumber = parseAsYouType(event.target.value, countryCodeValue);
      if (separatedPhoneNumber) {
        const { countryCode, nationalNumber } = separatedPhoneNumber;
        setFieldValue('countryCode', countryCode);
        setFieldValue('nationalNumber', nationalNumber);
      } else {
        handleChange(event);
      }
    },
    [handleChange, setFieldValue, countryCodeValue],
  );
  const handleCountryCodeSelect = useCallback(
    (countryCode) => {
      setFieldValue('countryCode', countryCode);
      toggleCountryCodeModalVisible();
    },
    [setFieldValue, toggleCountryCodeModalVisible],
  );

  return (
    <form onSubmit={handleSubmit}>
      <Title className={classes.centeredText} level={4}>
        {title}
      </Title>

      <div className={classes.nameContainer}>
        <Input
          {...inputProps('firstName', props)}
          label={t('user.firstName')}
          className={classes.firstName}
        />
        <Input
          {...inputProps('lastName', props)}
          label={t('user.lastName')}
          className={classes.lastName}
        />
      </div>


      <Select
        {...selectProps('countryCode', props)}
        options={dialCodesOptions}
        label={t('user.phoneNumber')}
        className={classes.countryCodeSelectDesktop}
      />
      <div className={classnames(classes.label, classes.countryCodeSelectMobile)}>
        {t('user.phoneNumber')}
      </div>
      <Button
        className={classes.countryCodeSelectMobile}
        onClick={toggleCountryCodeModalVisible}
      >
        {get(dialCodesOptionsByCountryCode, [props.values.countryCode, 'label'])}
      </Button>
      <Modal
        visible={countryCodeModalVisible}
        onOk={toggleCountryCodeModalVisible}
        onCancel={toggleCountryCodeModalVisible}
        cancelText={t('answerRequest.cancelText')}
        style={styles.modal}
      >
        <CountryCodeSelect
          countryCodes={dialCodesOptions}
          onSelect={handleCountryCodeSelect}
        />
      </Modal>
      <Input
        {...inputProps('nationalNumber', props)}
        type="tel"
        autoComplete="tel"
        onChange={handlePhoneNumberChange}
        addonBefore={dialCodesByCountryCode[values.countryCode]}
      />

      <BirthdayInput value={values.birthday} onSelect={handleSelect} />

      <SubmitButton
        isSubmitting={isSubmitting}
        isValid={isValid}
        className={classes.submitButton}
        block
      >
        {t('answerRequest.submit', { inviterName })}
      </SubmitButton>

      <Checkbox name="rememberMe" checked={rememberMe} onChange={handleChange}>
        {t('answerRequest.rememberMe')}
      </Checkbox>
    </form>
  );
}

AnswerRequestForm.propTypes = {
  classes: classesPropType.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  inviterName: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  values: PropTypes.shape({
    birthday: birthdayPropType,
    dialCode: PropTypes.string,
    countryCode: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    nationalNumber: PropTypes.string,
  }).isRequired,
};

const validationSchema = (({ t }) => {
  const requiredText = t('validations.required');
  return Yup.object().shape({
    birthday: Yup.object().shape({
      day: Yup.number().min(1).max(31).required(requiredText),
      month: Yup.number().min(1).max(12).required(requiredText),
      year: Yup.number().positive().max((new Date()).getFullYear()),
    }).required(),
    countryCode: Yup.string().required(requiredText),
    firstName: Yup.string().required(requiredText),
    lastName: Yup.string().required(requiredText),
    nationalNumber: Yup.string().required(requiredText).test(
      'phone-number-validity',
      t('validations.invalidPhoneNumber'),
      function validatePhoneNumber(value) {
        const { countryCode } = this.parent;
        return isValidNumber(`${dialCodesByCountryCode[countryCode]}${value}`);
      },
    ),
  });
});

function getInitialValues({ initialValues = {} }) {
  const values = { ...initialValues, rememberMe: true };
  if (initialValues.phoneNumber) {
    Object.assign(values, separateFormattedNumber(initialValues.phoneNumber));
  }
  values.birthday = initialValues.birthday || getDefaultBirthday(DEFAULT_NO_YEAR);
  return values;
}

export default compose(
  withTranslation(),
  withFormik({
    mapPropsToValues: getInitialValues,
    isInitialValid: (props) => {
      const { invitationType } = props;
      if (invitationType !== INVITATION_TYPES.direct) return false;
      const initialValues = getInitialValues(props);
      if (initialValues.firstName || initialValues.lastName) {
        return validationSchema(props).isValidSync(initialValues);
      }
      return true;
    },
    validationSchema,
    handleSubmit: (
      {
        countryCode,
        nationalNumber,
        rememberMe,
        ...rest
      },
      { props: { onSubmit }, setSubmitting },
    ) =>
      onSubmit(
        { ...rest, phoneNumber: formatNumber(`${dialCodesByCountryCode[countryCode]}${nationalNumber}`) },
        rememberMe,
      ).catch(() => setSubmitting(false)),
  }),
  injectSheet(styles),
)(AnswerRequestForm);
